import { Component, ErrorInfo, ReactNode } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level safety net wrapping the entire router. Catches any uncaught
 * render error from any route (marketing, app, admin) and shows a localized
 * fallback instead of a white screen. Lower boundaries (e.g. PrintErrorBoundary)
 * still catch route-local errors first.
 */
class GlobalErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[GlobalErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    document.body.classList.remove("pdf-exporting", "print-landscape");
    document.documentElement.classList.remove("pdf-exporting");
    this.setState({ hasError: false, error: null });
  };

  handleHome = () => {
    this.handleReset();
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { t } = this.props;
    const title = t("errors.renderTitle", { defaultValue: "تعذّر عرض هذه الصفحة" });
    const desc = t("errors.renderDesc", {
      defaultValue:
        "حدث خطأ غير متوقع أثناء تجهيز العرض. يمكنك المحاولة مرة أخرى أو العودة للصفحة الرئيسية.",
    });
    const retry = t("common.retry", { defaultValue: "إعادة المحاولة" });
    const home = t("common.home", { defaultValue: "الرئيسية" });

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center p-4 bg-background"
      >
        <div className="max-w-lg w-full rounded-lg border border-destructive/30 bg-destructive/5 p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-destructive">{title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
              {this.state.error?.message && (
                <pre className="mt-3 max-h-40 overflow-auto rounded bg-muted p-2 text-xs text-muted-foreground whitespace-pre-wrap">
                  {this.state.error.message}
                </pre>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={this.handleReset} className="gap-2">
                  <RefreshCw className="h-4 w-4" /> {retry}
                </Button>
                <Button size="sm" variant="ghost" onClick={this.handleHome} className="gap-2">
                  <Home className="h-4 w-4" /> {home}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const GlobalErrorBoundary = withTranslation()(GlobalErrorBoundaryInner);
export default GlobalErrorBoundary;
